// Check if the element is already defined to prevent "Already Declared" error
if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
      
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));

      this.setHeaderCartIconAccessibility();
      this.initUpsellHandler();
    }

    initUpsellHandler() {
      this.addEventListener('click', (event) => {
        if (!event.target.classList.contains('add-upsell-btn')) return;

        const btn = event.target;
        const container = btn.closest('.upsell-container');
        if (!container) return;

        const inputs = container.querySelectorAll('.char-box');
        const dots = container.querySelectorAll('.dot-toggle');
        const variantId = btn.getAttribute('data-variant-id');
        
        // 1. Check if at least one input has a value
        let hasValue = Array.from(inputs).some(input => input.value.trim() !== "");

        if (!hasValue) {
          // Highlight all boxes in red if totally empty
          inputs.forEach(input => input.classList.add('input-error'));
          return; 
        } else {
          // Clean up any existing error classes if validation passes
          inputs.forEach(input => input.classList.remove('input-error'));
        }

        // 2. Prepare Data (Initials Property)
        let initialsArray = [];
        inputs.forEach((input, index) => {
          const char = input.value.trim().toUpperCase();
          if (char !== "") {
            initialsArray.push(char);
            
            // Add dot if selected (dots[index] is the dot following current input)
            const dot = dots[index];
            if (dot && dot.checked) {
              initialsArray.push(".");
            }
          }
        });

        const properties = {
          'Initials': initialsArray.join('')
        };

        this.addUpsellToCart(variantId, properties, btn);
      });

      // Remove red border from ALL inputs when ANY input changes
      this.addEventListener('input', (event) => {
        if (event.target.classList.contains('char-box')) {
          const container = event.target.closest('.upsell-container');
          if (container) {
            const allInputs = container.querySelectorAll('.char-box');
            allInputs.forEach(input => input.classList.remove('input-error'));
          }
        }
      });

      // Auto-advance to the next input when a character is typed
      this.addEventListener('input', (event) => {
        if (event.target.classList.contains('char-box')) {
          const input = event.target;
          const container = input.closest('.upsell-container');
          
          // 1. Clear error states (Your existing logic)
          if (container) {
            const allInputs = container.querySelectorAll('.char-box');
            allInputs.forEach(i => i.classList.remove('input-error'));

            // 2. Shift focus logic
            // If the user typed something, move to the next box in the list
            if (input.value.length >= 1) {
              const inputs = Array.from(allInputs);
              const currentIndex = inputs.indexOf(input);
              const nextInput = inputs[currentIndex + 1];

              if (nextInput) {
                nextInput.focus();
              }
            }
          }
        }
      });

      this.addEventListener('keydown', (event) => {
        if (event.target.classList.contains('char-box') && event.key === 'Backspace') {
          const input = event.target;
          
          // If current box is empty, move focus to the previous box
          if (input.value === '') {
            const container = input.closest('.upsell-container');
            const inputs = Array.from(container.querySelectorAll('.char-box'));
            const prevInput = inputs[inputs.indexOf(input) - 1];

            if (prevInput) {
              prevInput.focus();
            }
          }
        }
      });

    }

    addUpsellToCart(variantId, properties, btn) {
      const id = parseInt(variantId);
      btn.classList.add('loading');
      btn.innerText = 'Adding...';

      

      const formData = {
        'id': id,
        'quantity': 1,
        'properties': properties
      };

      // 1. Add the item
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })
      .then((response) => {
        // We don't care if this specific response has a JSON error 
        // as long as the status is 200 (Success)
        if (response.ok) {
          return response.json().catch(() => ({})); // Silently handle non-JSON success
        }
        throw new Error('Failed to add');
      })
      .then(() => {
        // 2. FORCE REFRESH the drawer sections
        this.getUpdatedSections();
      })
      .catch((error) => {
        console.error('Upsell Error:', error);
      })
      .finally(() => {
        btn.classList.remove('loading');
        btn.innerText = 'Add';
      });
    }

    getUpdatedSections() {
      // We fetch the sections directly from the bundled renderer
      // This avoids the 'unexpected token <' error from add.js
      const sections = this.getSectionsToRender().map((section) => section.id).join(',');
      
      fetch(`${window.location.pathname}?sections=${sections}`)
        .then((response) => response.json())
        .then((parsedState) => {
          // Manually construct the state object for renderContents
          const renderState = {
            sections: parsedState
          };
          this.renderContents(renderState);
        })
        .catch((error) => console.error('Refresh Error:', error));
    }

    // ... Rest of your existing methods (open, close, renderContents, etc.) ...
    setHeaderCartIconAccessibility() {
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink) return;
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
    }

    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
      if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
      
      setTimeout(() => {
        this.classList.add('animate', 'active');
        const cartDrawerItems = this.querySelector('cart-drawer-items');
        if (cartDrawerItems) {
          if (typeof cartDrawerItems.updateCartUpsellToggleState === 'function') cartDrawerItems.updateCartUpsellToggleState();
          if (typeof cartDrawerItems.updateCartUpsellVisibility === 'function') cartDrawerItems.updateCartUpsellVisibility();
        }
      });

      document.body.classList.add('overflow-hidden');
    }

    close() {
      this.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
    }

    renderContents(parsedState) {
      const inner = this.querySelector('.drawer__inner');
      if (inner && inner.classList.contains('is-empty')) inner.classList.remove('is-empty');
      
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        if (sectionElement && parsedState.sections) {
          sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        }
      });

      setTimeout(() => {
        const overlay = this.querySelector('#CartDrawer-Overlay');
        if (overlay) overlay.addEventListener('click', this.close.bind(this));
        this.open();
      });
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const element = parsed.querySelector(selector);
      return element ? element.innerHTML : '';
    }

    getSectionsToRender() {
      return [
        { id: 'cart-drawer', selector: '#CartDrawer' },
        { id: 'cart-icon-bubble' }
      ];
    }

    setActiveElement(element) {
      this.activeElement = element;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}

// Same for CartDrawerItems
if (!customElements.get('cart-drawer-items')) {
  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }
      ];
    }
  }
  customElements.define('cart-drawer-items', CartDrawerItems);
}